import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { isEmpty } from "lodash";
import entries from "lodash/entries";
import type { FC } from "react";
import { useState } from "react";
import ImagesPicker from "../../components/imagesPicker/ImagesPicker";
import { useImages } from "../../media/useImages";
import type { ImageDTO } from "../../publicPackages/CardImage";
import type { PackageDayDescriptionType } from "./PackageDescriptionTypes";
import { packageDescriptionTypes } from "./PackageDescriptionTypes";
import PackageProducts from "./PackageProducts";

type DayImage = ImageDTO;

interface Day {
  readonly dayNumber: number;
  readonly title: string;
  readonly details: Partial<Record<PackageDayDescriptionType, string>>;
  readonly products: PackageProduct[];
  readonly image?: DayImage;
}

interface PackageProduct {
  readonly id: string;
  readonly optional: boolean;
  readonly options: (string | null)[];
}

interface DayDetailsProps {
  readonly value: Day["details"];
  readonly onChange: (value: Day["details"]) => void;
}

const DayDetails: FC<DayDetailsProps> = ({ value, onChange }) => {
  const [mode, setMode] = useState<PackageDayDescriptionType>("description");

  const handleChange = (newValue: string) => {
    onChange({ [mode]: newValue });
  };

  return (
    <Stack direction="column" spacing={1}>
      <ToggleButtonGroup
        value={mode}
        exclusive
        size="small"
        onChange={(_, newValue) => setMode(newValue)}
        aria-label="Rodzaj opisu dnia"
        style={{ display: "flex", flexWrap: "wrap" }}
      >
        {entries(packageDescriptionTypes).map(([key, label]) => (
          <ToggleButton
            value={key}
            key={key}
            title={value[key as PackageDayDescriptionType]}
            style={{
              whiteSpace: "nowrap",
              fontSize: "75%",
              paddingTop: 2,
              paddingBottom: 2,
              fontStyle: isEmpty(value[key as PackageDayDescriptionType]) ? "italic" : "normal",
            }}
          >
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <TextField
        fullWidth
        multiline
        minRows={4}
        data-testid={`day-${mode}-input`}
        value={value[mode] ?? ""}
        onChange={(e) => handleChange(e.target.value)}
      />
    </Stack>
  );
};

interface PackageDayProps {
  day: Day;
  onChange: (day: Day) => void;
  onDelete: () => void;
}

const PackageDay: FC<PackageDayProps> = ({ day, onChange, onDelete }) => {
  const { images, isLoading } = useImages();

  const handleChange = (change: Partial<Day>) => {
    onChange({ ...day, ...change });
  };

  return (
    <Card>
      <CardHeader
        title={`Dzień ${day.dayNumber}`}
        action={
          <IconButton aria-label="usuń dzień" color="secondary" onClick={onDelete}>
            <DeleteIcon />
          </IconButton>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, xl: 8 }}>
            <PackageProducts products={day.products ?? []} onChange={(products) => onChange({ ...day, products })} />
          </Grid>
          <Grid container size={{ xs: 12, xl: 4 }} spacing={2}>
            <Grid size={12}>
              <TextField
                label="Nazwa dnia"
                fullWidth
                value={day.title}
                onChange={(e) => handleChange({ title: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <DayDetails
                value={day.details}
                onChange={(details) =>
                  handleChange({
                    details: {
                      ...day.details,
                      ...details,
                    },
                  })
                }
              />
            </Grid>
            <Grid size={12}>
              <ImagesPicker
                label="Ilustracja dnia"
                images={images}
                isLoading={isLoading}
                limit={1}
                selectedImages={day.image ? [day.image] : []}
                onSelectionChange={(images) => handleChange({ image: images.length === 0 ? undefined : images[0] })}
              />
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

interface PackageDaysProps {
  readonly days: Day[];
  readonly onChange: (days: Day[]) => void;
}

const PackageDays: FC<PackageDaysProps> = ({ days, onChange }) => {
  const handleAddDay = () => {
    const maxDay = days.length > 0 ? Math.max(...days.map((d) => d.dayNumber)) : null;
    const dayNumber = maxDay === null ? 1 : maxDay + 1;
    onChange([...days, { dayNumber, products: [], title: "", details: { description: "", plan: "" } }]);
  };

  const handleAddDayAtBeginning = () => {
    onChange([
      { dayNumber: 1, products: [], title: "", details: { description: "", plan: "" } },
      ...days.map((d) => ({ ...d, dayNumber: d.dayNumber + 1 })),
    ]);
  };

  return (
    <Stack direction="column" spacing={1}>
      {days.map((d, idx) => (
        <PackageDay
          key={d.dayNumber}
          day={d}
          onChange={(v) => onChange(days.map((day, dayIndex) => (dayIndex === idx ? v : day)))}
          onDelete={() =>
            onChange(
              days
                .filter((_, dayIndex) => dayIndex !== idx)
                .map((d, dayIndex) => (dayIndex < idx ? d : { ...d, dayNumber: d.dayNumber - 1 })),
            )
          }
        />
      ))}
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" color="primary" onClick={handleAddDay}>
          Dodaj dzień
        </Button>
        <Button variant="outlined" color="primary" onClick={handleAddDayAtBeginning}>
          Dodaj dzień 1
        </Button>
      </Stack>
    </Stack>
  );
};

export default PackageDays;
